/** tm/legal/LegalTopicsTab.tsx — 설정 「주제」 탭: 주제 목록 + 편집기 (새 주제 추가) */
import { useEffect, useState } from 'react';
import { C, T, R } from '../components/ui';
import { getKeywords, getSources, getSynonyms } from './legalApi';
import { chip, errMsg, MUTED } from './legalTheme';
import { Spinner } from './legalUi';
import LegalTopicEditor from './LegalTopicEditor';
import type { LegalKeyword, LegalSource, SynonymData } from './legalTypes';
import type { SettingsGuard } from './settingsGuard';

interface Props { mobile: boolean; onToast: (m: string, k?: 'ok' | 'error') => void; guard?: SettingsGuard }
type Pick = number | 'new' | null;

export default function LegalTopicsTab({ mobile, onToast, guard }: Props) {
  const [rows, setRows] = useState<LegalKeyword[] | null>(null);
  const [sources, setSources] = useState<LegalSource[]>([]);
  const [syn, setSyn] = useState<SynonymData>({ groups: [], abbr: [] });
  const [pick, setPickRaw] = useState<Pick>(null);
  /** 다른 주제로 옮길 때 저장 안 한 변경이 있으면 모달 막대로 */
  const setPick = (p: Pick) => { if (p === pick) return; if (guard) guard.request(() => setPickRaw(p)); else setPickRaw(p); };
  const [err, setErr] = useState('');
  useEffect(() => {
    getKeywords().then(r => { setRows(r); setPickRaw(p => (p === null && r.length ? r[0].id : p)); }).catch(e => setErr(errMsg(e, '주제를 불러오지 못했습니다')));
    getSources().then(setSources).catch(() => undefined);
    getSynonyms().then(setSyn).catch(() => undefined);
  }, []);
  if (err) return <div role="alert" style={{ color: C.red, fontSize: T.sm, padding: 20 }}>{err}</div>;
  if (!rows) return <div style={{ color: MUTED, fontSize: T.sm, padding: 20 }}><Spinner /> 불러오는 중…</div>;

  const cur = typeof pick === 'number' ? rows.find(r => r.id === pick) || null : null;
  const item = (label: string, on: boolean, off: boolean, onClick: () => void, key: string) => (
    <button key={key} type="button" className={on ? 'lw-f' : 'lw-f lw-fi'} onClick={onClick} aria-current={on ? 'true' : undefined} style={mobile ? chip(on) : {
      display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 0, borderRadius: R.sm, fontFamily: 'inherit', cursor: 'pointer',
      background: on ? C.accentLt : 'transparent', color: on ? C.accent : off ? MUTED : C.sub, fontWeight: on ? 700 : 400, fontSize: T.sm,
      textDecoration: off ? 'line-through' : 'none',
    }}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 10 : 16, minHeight: 0 }}>
      <nav aria-label="주제 목록" className="lw-noscroll" style={mobile
        ? { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }
        : { width: 190, flexShrink: 0, borderRight: `1px solid ${C.border}`, paddingRight: 10 }}>
        {rows.map(r => item(`${r.label}${r.feed ? ' · 자동' : ''}${r.alert === false ? ' 🔕' : ''}`, pick === r.id, !r.active, () => setPick(r.id), String(r.id)))}
        <button type="button" className="lw-f" onClick={() => setPick('new')} style={{
          ...(mobile ? chip(pick === 'new') : { display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', borderRadius: R.sm, fontSize: T.sm, fontFamily: 'inherit', cursor: 'pointer' }),
          border: `1px dashed ${pick === 'new' ? C.accent : C.borderDk}`, color: C.accent, background: pick === 'new' ? C.accentLt : C.white, marginTop: mobile ? 0 : 6, fontWeight: 600,
        }}>+ 주제 추가</button>
      </nav>
      <div style={{ flex: 1, minWidth: 0 }}>
        {pick === null ? <div style={{ color: MUTED, fontSize: T.sm }}>왼쪽에서 주제를 고르세요.</div> : (
          <LegalTopicEditor kw={cur} sources={sources} synonyms={syn.groups} abbr={syn.abbr} onToast={onToast} guard={guard}
            onSaved={k => { setRows(prev => (prev ? (prev.some(r => r.id === k.id) ? prev.map(r => (r.id === k.id ? k : r)) : [...prev, k]) : [k])); setPickRaw(k.id); }}
            onDeleted={id => { setRows(prev => (prev || []).filter(r => r.id !== id)); setPickRaw(null); }} />
        )}
      </div>
    </div>
  );
}
